// Copyright (C) 2021 Bystronic Laser AG

import axios from 'axios';
import { BaseUrl } from '@/models/constants';
import {
  AlertAction,
  AlertKPI,
  AlertKPIGroup,
  CareAlertMetric,
  WccAlertMetric,
} from '@/models/enums/AlertsEnums';
import { usePersistentStore } from '@/store/persistent';
import { CareAlert } from '@/models/careAlert';
import { WccAlert } from '@/models/wccAlert';
import { AlertsDefinition } from '@/models/alertsDefinition';

class AlertsService {
  store = new (class {
    wccKpiGroups() {
      return usePersistentStore().wccKpiGroups;
    }

    careKpiGroups() {
      return usePersistentStore().careKpiGroups;
    }

    getWccAlertKpiByMetric(metric: WccAlertMetric): AlertKPI<WccAlertMetric> {
      return usePersistentStore().getWccAlertKpiByMetric(metric)!;
    }

    getCareAlertKpiByMetric(
      metric: CareAlertMetric,
    ): AlertKPI<CareAlertMetric> {
      return usePersistentStore().getCareAlertKpiByMetric(metric)!;
    }

    /**
     * Returns the unit assigned to the specified WCC alert metric.
     *
     * @param metric Alert metric (OEE or LaserHealth) to get the unit.
     */
    getWccAlertMetricUnit(metric: WccAlertMetric): string | null {
      return this.getWccAlertKpiByMetric(metric).unit;
    }

    /**
     * Returns the unit assigned to the specified Care Console alert metric.
     *
     * @param metric Alert metric like Temperatures, ErrorCount, etc.to get the
     * unit.
     */
    getCareAlertMetricUnit(metric: CareAlertMetric): string | null {
      return this.getCareAlertKpiByMetric(metric).unit;
    }

    updateWccKpiGroups(kpiGroups: AlertKPIGroup<WccAlertMetric>[]) {
      usePersistentStore().setWccKpiGroups(kpiGroups);
    }

    updateCareKpiGroups(kpiGroups: AlertKPIGroup<CareAlertMetric>[]) {
      usePersistentStore().setCareKpiGroups(kpiGroups);
    }
  })();

  getCareConsoleAlerts(): Promise<CareAlert[]> {
    return axios
      .get<CareAlert[]>(`${BaseUrl}/alerts/cc`)
      .then((response: any) => {
        return response.data;
      });
  }

  getWorkCenterConsoleAlerts(tenantId: number): Promise<WccAlert[]> {
    return axios
      .get<WccAlert[]>(`${BaseUrl}/alerts/wcc?tenantId=${tenantId}`)
      .then((response: any) => {
        return response.data;
      });
  }

  async deleteCustomAlert(id: number) {
    await axios.delete(`${BaseUrl}/alerts/cc/${id}`);
  }

  async deleteWccAlert(id: number) {
    await axios.delete(`${BaseUrl}/alerts/wcc/${id}`);
  }

  async updateCustomAlert(alert: CareAlert) {
    await axios.put(`${BaseUrl}/alerts/cc`, alert);
  }

  async updateWccAlert(alert: WccAlert) {
    await axios.put(`${BaseUrl}/alerts/wcc`, alert);
  }

  async create(alert: WccAlert) {
    await axios.post(`${BaseUrl}/alerts/wcc`, alert).then((response: any) => {
      return response.data;
    });
  }

  async createMultiple(alertsDefinition: AlertsDefinition) {
    await axios
      .post(`${BaseUrl}/alerts/cc`, alertsDefinition)
      .then((response: any) => {
        return response.data;
      });
  }

  getAlertEmail(alertId: number): Promise<string[]> {
    return axios
      .get<string[]>(`${BaseUrl}/alerts/emailrecipients/${alertId}`)
      .then((response) => {
        return response.data;
      });
  }

  async getWccAlertKpis(): Promise<AlertKPIGroup<WccAlertMetric>[]> {
    const alertKpis = await axios
      .get<AlertKPIGroup<WccAlertMetric>[]>(`${BaseUrl}/alerts/kpis/wcc`)
      .then((response) => response.data);
    this.store.updateWccKpiGroups(alertKpis);
    return alertKpis;
  }

  async getCareAlertKpis(): Promise<AlertKPIGroup<CareAlertMetric>[]> {
    const alertKpis = await axios
      .get<AlertKPIGroup<CareAlertMetric>[]>(`${BaseUrl}/alerts/kpis/cc`)
      .then((response) => response.data);
    this.store.updateCareKpiGroups(alertKpis);
    return alertKpis;
  }

  subscribe(equipmentId: number): Promise<void> {
    return this.subscribeAll([equipmentId]);
  }

  unsubscribe(equipmentId: number): Promise<void> {
    return this.unsubscribeAll([equipmentId]);
  }

  async subscribeAll(selectedEquipmentIds: number[]): Promise<void> {
    return axios.put(`${BaseUrl}/alerts/equipment/subscribe`, {
      action: AlertAction.WebAndEmail,
      ids: selectedEquipmentIds,
    });
  }

  async unsubscribeAll(selectedEquipmentIds: number[]): Promise<void> {
    return axios.put(
      `${BaseUrl}/alerts/equipment/unsubscribe`,
      selectedEquipmentIds,
    );
  }
}

export const alertsService = new AlertsService();
